import React from 'react';

import { SETTINGS_PAGE_TITLES, type SettingsPageId } from '@/lib/settingsPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import { SmallButton } from '@/pages/account/primitives';

/**
 * AR-390 — the page shell every Settings page renders inside.
 *
 * The same shape the Account center's shell has, less the two things that are
 * Account's: there is no action-required notice here (it is the Account
 * center's replacement for the overview it lost), and no page in this module
 * publishes, so the save bar only ever carries Save all and Discard.
 */

export const SettingsSaveBar: React.FC = () => {
  const { openCards, saveAll, cancelAll } = useAccountRecord();
  if (!openCards.size) return null;
  return (
    <div
      data-settings-savebar
      className="sticky top-0 z-20 my-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-300 bg-white px-3.5 py-2 text-[14.5px] font-semibold text-gray-900 shadow-md dark:border-orange-700 dark:bg-[#1A2021] dark:text-gray-100"
    >
      <span>Unsaved changes on this page</span>
      <span className="flex items-center gap-2">
        <SmallButton primary onClick={saveAll}>
          Save all
        </SmallButton>
        <SmallButton onClick={cancelAll}>Discard</SmallButton>
      </span>
    </div>
  );
};

/**
 * The line that says what the reader is looking at — the Account center's
 * wording, because it is the same record under both columns.
 */
export const SettingsSampleFooter: React.FC = () => {
  // AR-391 — the Settings module has its own binding and its own flag, so
  // the footer reads the module's answer rather than the Account center's.
  const { reset, isSettingsLive } = useAccountRecord();
  if (isSettingsLive) {
    return (
      <p className="mt-3.5 text-[14px] text-gray-500 dark:text-gray-400">
        These are your own settings. Fields WorldVia does not hold yet still show
        sample values.
      </p>
    );
  }
  return (
    <p className="mt-3.5 text-[14px] text-orange-800 dark:text-orange-400">
      You can edit this sample data.{' '}
      <button
        type="button"
        onClick={reset}
        className="cursor-pointer underline hover:no-underline"
      >
        Click here to reset form.
      </button>
    </p>
  );
};

export const SettingsPageShell: React.FC<{
  page: SettingsPageId;
  /** Controls in the page header. */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ page, headerActions, children }) => {
  const [title, standfirst] = SETTINGS_PAGE_TITLES[page];

  return (
    <div className="px-5 py-5 lg:px-8" data-settings-page={page}>
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          <p className="mt-0.5 text-[15.5px] text-gray-500 dark:text-gray-400">
            {standfirst}
          </p>
        </div>
        {headerActions ? (
          <div className="flex flex-wrap items-center gap-1.5">{headerActions}</div>
        ) : null}
      </div>
      <SettingsSaveBar />
      {children}
      <SettingsSampleFooter />
    </div>
  );
};
